import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Boxes, Scan, CheckCircle2, X, Trash2, MapPin, Settings, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const getUser = () => {
  try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
};
const isAdmin = () => {
  const r = (getUser().role || '').toLowerCase();
  return r.includes('admin');
};

const FGCreatePack = () => {
  const navigate = useNavigate();
  const pieceRef = useRef(null);
  const codeRef = useRef(null);
  const paCodeRef = useRef(null);
  const paBinRef = useRef(null);
  const staffRef = useRef(null);

  const [tab, setTab] = useState('create'); // create | putaway
  const [packSize, setPackSize] = useState(5);
  const [sizeDraft, setSizeDraft] = useState('5');
  const [savingSize, setSavingSize] = useState(false);

  // Staff gate
  const [staff, setStaff] = useState(null); // {staff_id, staff_name}
  const [staffInput, setStaffInput] = useState('');
  const [validatingStaff, setValidatingStaff] = useState(false);

  // Create pack state
  const [pieces, setPieces] = useState([]); // [{usn_code, sku_code, design_name, shade, size}]
  const [pieceInput, setPieceInput] = useState('');
  const [packCode, setPackCode] = useState('');
  const [scanning, setScanning] = useState(false);
  const [creating, setCreating] = useState(false);
  const lockSku = pieces[0]?.sku_code || '';
  const product = pieces[0] || null;

  // Put-away state
  const [paCode, setPaCode] = useState('');
  const [paPack, setPaPack] = useState(null);
  const [paBin, setPaBin] = useState('');
  const [paLoading, setPaLoading] = useState(false);
  const [paSubmitting, setPaSubmitting] = useState(false);
  const [paResult, setPaResult] = useState(null);

  const loadConfig = useCallback(async () => {
    try {
      const r = await apiClient.get('/wms/fg/packs/config');
      setPackSize(r.data.pack_size || 5);
      setSizeDraft(String(r.data.pack_size || 5));
    } catch (e) { /* non-fatal */ }
  }, []);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  useEffect(() => {
    if (!staff) { setTimeout(() => staffRef.current?.focus(), 100); return; }
    if (tab === 'create') setTimeout(() => pieceRef.current?.focus(), 100);
    if (tab === 'putaway') setTimeout(() => paCodeRef.current?.focus(), 100);
  }, [staff, tab]);

  // --- staff gate ---
  const submitStaff = async (e) => {
    e?.preventDefault();
    const id = staffInput.trim();
    if (!id || validatingStaff) return;
    setValidatingStaff(true);
    try {
      const r = await apiClient.post('/wms/fg/packs/validate-staff', { staff_id: id });
      setStaff(r.data);
      setStaffInput('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Staff not found');
    } finally {
      setValidatingStaff(false);
    }
  };

  // --- admin pack size ---
  const saveSize = async () => {
    const n = parseInt(sizeDraft, 10);
    if (!n || n < 1) { toast.error('Enter a valid pack size'); return; }
    setSavingSize(true);
    try {
      await apiClient.put('/wms/fg/packs/config', { pack_size: n });
      setPackSize(n);
      toast.success(`Standard pack size set to ${n}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save pack size');
    } finally {
      setSavingSize(false);
    }
  };

  // --- create: scan a piece ---
  const scanPiece = async (e) => {
    e?.preventDefault();
    const usn = pieceInput.trim();
    if (!usn || scanning) return;
    if (pieces.some((p) => p.usn_code === usn)) {
      toast.error(`${usn} already scanned`);
      setPieceInput('');
      return;
    }
    if (pieces.length >= packSize) {
      toast.error(`Pack full (${packSize}) — scan the pack code to finish`);
      setPieceInput('');
      setTimeout(() => codeRef.current?.focus(), 80);
      return;
    }
    setScanning(true);
    try {
      const r = await apiClient.post('/wms/fg/packs/validate-piece', { usn_code: usn, lock_sku: lockSku });
      const next = [...pieces, r.data];
      setPieces(next);
      setPieceInput('');
      if (next.length >= packSize) {
        toast.success('Pack complete — scan the pack code');
        setTimeout(() => codeRef.current?.focus(), 80);
      } else {
        setTimeout(() => pieceRef.current?.focus(), 80);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Piece not eligible');
      setPieceInput('');
    } finally {
      setScanning(false);
    }
  };

  const removePiece = (usn) => {
    setPieces((prev) => prev.filter((p) => p.usn_code !== usn));
    setTimeout(() => pieceRef.current?.focus(), 80);
  };

  const createPack = async (e) => {
    e?.preventDefault();
    const code = packCode.trim();
    if (!code) { toast.error('Scan the pre-printed pack code'); codeRef.current?.focus(); return; }
    if (!pieces.length) { toast.error('Scan at least one piece first'); return; }
    if (creating) return;
    setCreating(true);
    try {
      const r = await apiClient.post('/wms/fg/packs/create', {
        pack_code: code,
        usn_codes: pieces.map((p) => p.usn_code),
        staff_id: staff?.staff_id,
        staff_name: staff?.staff_name,
      });
      toast.success(r.data.message || 'Pack created');
      setPieces([]);
      setPackCode('');
      setTimeout(() => pieceRef.current?.focus(), 80);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create pack');
    } finally {
      setCreating(false);
    }
  };

  // --- put-away ---
  const loadPaPack = async (e) => {
    e?.preventDefault();
    const code = paCode.trim();
    if (!code || paLoading) return;
    setPaLoading(true);
    setPaResult(null);
    try {
      const r = await apiClient.get(`/wms/fg/packs/${encodeURIComponent(code)}`);
      setPaPack(r.data);
      if (r.data.status === 'NEW' && r.data.suggested_bin) {
        setPaBin(r.data.suggested_bin);
      }
      setTimeout(() => paBinRef.current?.focus(), 80);
    } catch (err) {
      setPaPack(null);
      toast.error(err.response?.data?.detail || 'Pack not found');
    } finally {
      setPaLoading(false);
    }
  };

  const submitPutAway = async (e) => {
    e?.preventDefault();
    const bin = paBin.trim();
    if (!paPack) { toast.error('Scan a pack code first'); return; }
    if (!bin) { toast.error('Scan the destination bin'); paBinRef.current?.focus(); return; }
    if (paSubmitting) return;
    setPaSubmitting(true);
    try {
      const r = await apiClient.post('/wms/fg/packs/put-away', {
        pack_code: paPack.pack_code,
        bin_code: bin,
        staff_id: staff?.staff_id,
        staff_name: staff?.staff_name,
      });
      setPaResult(r.data);
      toast.success(r.data.message || 'Pack stored');
      setPaPack(null);
      setPaCode('');
      setPaBin('');
      setTimeout(() => paCodeRef.current?.focus(), 80);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Put-away failed');
    } finally {
      setPaSubmitting(false);
    }
  };

  // ---------- STAFF GATE ----------
  if (!staff) {
    return (
      <div className="min-h-screen bg-[#FFFBF5] flex flex-col">
        <header className="bg-[#1A1F2E] text-[#F9FAFB] px-4 h-16 flex items-center">
          <Button variant="ghost" onClick={() => navigate('/wms/sfg')} className="text-[#F9FAFB] hover:bg-white/10 mr-3" data-testid="fgpack-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <Boxes className="w-6 h-6" />
            <h1 className="text-lg font-bold">Create Pack</h1>
          </div>
        </header>
        <div className="flex-1 flex flex-col items-center justify-center px-6">
          <Card className="w-full max-w-sm">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold text-center mb-4">Scan your Staff ID</h2>
              <form onSubmit={submitStaff} className="space-y-3">
                <Input ref={staffRef} value={staffInput} onChange={(e) => setStaffInput(e.target.value)}
                  placeholder="Staff ID" className="text-center text-lg h-12" data-testid="fgpack-staff-input" autoFocus />
                <Button type="submit" className="w-full h-11 bg-fuchsia-600 hover:bg-fuchsia-700" disabled={validatingStaff} data-testid="fgpack-staff-submit">
                  {validatingStaff ? 'Checking…' : 'Start'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ---------- MAIN ----------
  return (
    <div className="min-h-screen bg-[#FFFBF5] flex flex-col">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate('/wms/sfg')} className="text-[#F9FAFB] hover:bg-white/10" data-testid="fgpack-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <Boxes className="w-6 h-6" />
            <div>
              <h1 className="text-lg font-bold leading-tight">Create Pack</h1>
              <p className="text-[11px] text-[#E8DCC8]">{staff.staff_name} ({staff.staff_id})</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] bg-white/10 px-2 py-1 rounded" data-testid="fgpack-size-value">Pack size: {packSize}</span>
          <Button size="sm" variant="ghost" className="text-[#F9FAFB] hover:bg-white/10 text-xs" onClick={() => { setStaff(null); }} data-testid="fgpack-switch-staff">Switch</Button>
        </div>
      </header>

      {/* Tabs */}
      <div className="flex border-b border-[#E5DFD0] bg-white">
        <button onClick={() => setTab('create')} className={`flex-1 py-3 text-sm font-semibold ${tab === 'create' ? 'text-fuchsia-700 border-b-2 border-fuchsia-600' : 'text-[#5A5F6E]'}`} data-testid="fgpack-tab-create">Create Pack</button>
        <button onClick={() => setTab('putaway')} className={`flex-1 py-3 text-sm font-semibold ${tab === 'putaway' ? 'text-fuchsia-700 border-b-2 border-fuchsia-600' : 'text-[#5A5F6E]'}`} data-testid="fgpack-tab-putaway">Put Away Pack</button>
      </div>

      {/* Admin pack size */}
      {isAdmin() && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center gap-2" data-testid="fgpack-admin-config">
          <Settings className="w-4 h-4 text-amber-700" />
          <span className="text-xs text-amber-800">Standard pack size</span>
          <Input type="number" value={sizeDraft} onChange={(e) => setSizeDraft(e.target.value)} className="h-8 w-20 text-center" data-testid="fgpack-size-input" />
          <Button size="sm" className="h-8 bg-amber-600 hover:bg-amber-700" onClick={saveSize} disabled={savingSize} data-testid="fgpack-size-save">
            {savingSize ? '…' : 'Save'}
          </Button>
        </div>
      )}

      <main className="flex-1 px-4 py-4 max-w-md mx-auto w-full">
        {tab === 'create' && (
          <div className="space-y-4">
            {/* product line */}
            {product && (
              <div className="bg-white rounded-lg border border-[#E5DFD0] p-3" data-testid="fgpack-product-line">
                <p className="text-xs text-[#5A5F6E]">Product (locked for this pack)</p>
                <p className="font-semibold text-[#1A1F2E]">{product.design_name || product.sku_code}</p>
                <p className="text-sm text-[#5A5F6E]">{product.sku_code} · {product.shade} · {product.size}</p>
              </div>
            )}

            {/* count */}
            <div className="flex items-center justify-between bg-fuchsia-50 border border-fuchsia-200 rounded-lg px-4 py-3">
              <span className="text-sm text-fuchsia-800 font-medium">Scanned</span>
              <span className="text-2xl font-bold text-fuchsia-700" data-testid="fgpack-piece-count">{pieces.length} / {packSize}</span>
            </div>

            {/* scan piece */}
            <form onSubmit={scanPiece} className="flex gap-2">
              <Input ref={pieceRef} value={pieceInput} onChange={(e) => setPieceInput(e.target.value)}
                placeholder={scanning ? 'Checking…' : 'Scan piece sticker'} className="h-12 text-lg" data-testid="fgpack-piece-input" disabled={scanning || pieces.length >= packSize} />
              <Button type="submit" className="h-12 bg-fuchsia-600 hover:bg-fuchsia-700" disabled={scanning || pieces.length >= packSize} data-testid="fgpack-piece-scan-btn">
                <Scan className="w-5 h-5" />
              </Button>
            </form>

            {/* pieces list */}
            {pieces.length > 0 && (
              <div className="space-y-1.5">
                {pieces.map((p) => (
                  <div key={p.usn_code} className="flex items-center justify-between bg-white border border-[#E5DFD0] rounded px-3 py-2" data-testid={`fgpack-piece-row-${p.usn_code}`}>
                    <span className="font-mono text-sm">{p.usn_code}</span>
                    <button onClick={() => removePiece(p.usn_code)} className="text-red-500" data-testid={`fgpack-piece-remove-${p.usn_code}`}>
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* pack code + create */}
            <div className="bg-white rounded-lg border-2 border-fuchsia-300 p-3 space-y-2">
              <p className="text-xs font-medium text-[#5A5F6E]">Scan the pre-printed pack code</p>
              <form onSubmit={createPack} className="flex gap-2">
                <Input ref={codeRef} value={packCode} onChange={(e) => setPackCode(e.target.value)}
                  placeholder="Pack QR code" className="h-12 text-lg" data-testid="fgpack-code-input" />
                <Button type="submit" className="h-12 bg-emerald-600 hover:bg-emerald-700 whitespace-nowrap" disabled={creating || !pieces.length} data-testid="fgpack-create-btn">
                  <CheckCircle2 className="w-5 h-5 mr-1" /> Create
                </Button>
              </form>
              {pieces.length > 0 && pieces.length < packSize && (
                <p className="text-[11px] text-amber-700" data-testid="fgpack-short-note">
                  Short pack: {pieces.length} of {packSize}. Scan the pack code to finish a short pack.
                </p>
              )}
            </div>
          </div>
        )}

        {tab === 'putaway' && (
          <div className="space-y-4">
            <form onSubmit={loadPaPack} className="flex gap-2">
              <Input ref={paCodeRef} value={paCode} onChange={(e) => setPaCode(e.target.value)}
                placeholder="Scan pack code" className="h-12 text-lg" data-testid="fgpack-pa-code-input" />
              <Button type="submit" className="h-12 bg-fuchsia-600 hover:bg-fuchsia-700" disabled={paLoading} data-testid="fgpack-pa-load-btn">
                <Scan className="w-5 h-5" />
              </Button>
            </form>

            {paPack && (
              <div className="bg-white rounded-lg border border-[#E5DFD0] p-3 space-y-2" data-testid="fgpack-pa-members">
                <div className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-fuchsia-600" />
                  <div>
                    <p className="font-semibold">{paPack.pack_code}</p>
                    <p className="text-sm text-[#5A5F6E]">{paPack.design_name || paPack.sku_code} · {paPack.shade} · {paPack.size}</p>
                  </div>
                </div>
                <p className="text-sm">
                  <span className="font-bold text-fuchsia-700">{paPack.total_members}</span> piece(s) · status <span className="font-medium">{paPack.status}</span>
                </p>
                {paPack.status !== 'NEW' && (
                  <p className="text-xs text-amber-700">This pack is already {paPack.status.toLowerCase()}{paPack.bin_code ? ` in ${paPack.bin_code}` : ''}.</p>
                )}
                {paPack.status === 'NEW' && paPack.suggested_bin && (
                  <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5" data-testid="fgpack-pa-bin-suggestion">
                    Suggested bin: <span className="font-bold">{paPack.suggested_bin}</span>
                    {paPack.bin_info?.reason ? ` — ${paPack.bin_info.reason}` : ''}
                  </p>
                )}
                <form onSubmit={submitPutAway} className="space-y-2 pt-1">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-[#5A5F6E]" />
                    <Input ref={paBinRef} value={paBin} onChange={(e) => setPaBin(e.target.value)}
                      placeholder="Scan destination bin" className="h-11" data-testid="fgpack-pa-bin-input" disabled={paPack.status !== 'NEW'} />
                  </div>
                  <Button type="submit" className="w-full h-11 bg-emerald-600 hover:bg-emerald-700" disabled={paSubmitting || paPack.status !== 'NEW'} data-testid="fgpack-pa-submit">
                    {paSubmitting ? 'Storing…' : `Put away ${paPack.total_members} piece(s)`}
                  </Button>
                </form>
              </div>
            )}

            {paResult && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid="fgpack-pa-result">
                <p className="text-emerald-800 font-medium">{paResult.message}</p>
                {paResult.rejected_count > 0 && (
                  <p className="text-xs text-amber-700 mt-1">{paResult.rejected_count} piece(s) not placed.</p>
                )}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default FGCreatePack;
